import React from 'react';
import { Mui, MuiIcons } from '@osu/react-ui';
import { STEP_STATUS } from '../../utils/constants';

/**
 * StepIcon Component for V2
 * Displays appropriate icon based on step status and type
 */
const mediumGray = `grey.600`
const darkGray = `grey.800`

const STEP_ICONS = {
  review: MuiIcons.HourglassEmpty,
  decision: MuiIcons.Gavel,
  invitation: MuiIcons.Mail,
  preferences: MuiIcons.Assignment,
  "honors-complete": MuiIcons.Gavel,
};

const STEP_ICON_COLORS = {
  invitation: `warning`,
  "honors-complete": `primary`,
};

const COMPLETED_STEP_ICONS = {
  review: MuiIcons.CheckCircle,
  invitation: MuiIcons.CheckCircle,
  decision: MuiIcons.CheckCircle,
};
const COMPLETED_STEP_ICON_COLORS = {
  "decision": `success`,
  "invitation": `success`,
};

const getStepType = (id) => {
  if(!id) {
    return ""
  }
  const lcId = (id || "").toLowerCase()
  return Object.keys(STEP_ICONS).find(type => {
    return lcId.includes(type.toLowerCase())
  });
};

// Named icon overrides that a step can request explicitly (has607 — e.g. a gavel on a completed
// deny decision instead of the default green check mark).
const ICON_OVERRIDES = {
  gavel: MuiIcons.Gavel,
};

const getIconAndColor = (options = {}) => {
    const {
    id,
    status,
    isActive,
    showPreferencesForm,
    showGrayedOut,
    iconOverride
  } = options
  const stepType = getStepType(id);
  const overrideIcon = iconOverride ? ICON_OVERRIDES[iconOverride] : null;
  let icon = STEP_ICONS?.[stepType]
  const showGrayedOutValues = {
    backgroundColor: darkGray,
    opacity: ' 0.6',
  }
  let color = STEP_ICON_COLORS?.[stepType]
  let backgroundColor = color ? color + ".main" : ""
  // Complete status
  if (status === STEP_STATUS.COMPLETE || status === STEP_STATUS.EXTERNAL_COMPLETE) {

    if(COMPLETED_STEP_ICON_COLORS?.[stepType]) {
      color = COMPLETED_STEP_ICON_COLORS?.[stepType]
      backgroundColor = color + ".main"
    }
    if(COMPLETED_STEP_ICONS?.[stepType]) {
      icon = COMPLETED_STEP_ICONS?.[stepType]
    }
    return {      
      icon: overrideIcon || icon || MuiIcons.CheckCircle,
      color: color || 'success',
      backgroundColor: backgroundColor || 'success.main', // Use theme color for better accessibility
      opacity: 1
    };
  }

  // In progress or active status
  if (status === STEP_STATUS.IN_PROGRESS || isActive) {
    const isPreferencesActive = stepType === 'preferences' && showPreferencesForm;
    
    return {
      icon: overrideIcon || icon || MuiIcons.RadioButtonChecked,
      color: isPreferencesActive ? 'error' : (color || "primary"),
      backgroundColor: isPreferencesActive ? '#BA0C2F' : (backgroundColor || "primary.main"),
      opacity: 1
    };
  }

  // Pending status
  return {
    icon: overrideIcon || icon || MuiIcons.RadioButtonUnchecked,
    color: 'grey',
    backgroundColor: showGrayedOut ? showGrayedOutValues?.backgroundColor : mediumGray,
    opacity: showGrayedOut ? showGrayedOutValues?.opacity : 1
  };
};


function StepIcon({ step }) {
  const { status, id, isActive, showGrayedOut, iconOverride } = step;
  

  const { icon: IconComponent, color, backgroundColor, opacity } = getIconAndColor({
    id,
    status,
    isActive,
    showPreferencesForm: step?.showPreferencesForm,
    showGrayedOut,
    iconOverride
  });
  
  // Determine if this step should have a colored background
  const hasColoredBackground = status === STEP_STATUS.COMPLETE || status === STEP_STATUS.EXTERNAL_COMPLETE || status === STEP_STATUS.IN_PROGRESS || isActive;

  return (
    <Mui.Box
      sx={{
        // has593v2 — moderate mobile shrink. xs sizes are smaller for phones; sm+ keeps the original
        // 56/48 so desktop is unchanged.
        // has593v2 — tablet (sm) is slightly smaller; desktop (md+) keeps the original 56/48.
        // Phones (xs) don't render the icon at all (hidden by the wrapper in VerticalTimelineStep),
        // so no xs size is needed here.
        width: isActive ? { sm: 48, md: 56 } : { sm: 40, md: 48 },
        height: isActive ? { sm: 48, md: 56 } : { sm: 40, md: 48 },
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: backgroundColor || 'background.paper', // Ensure solid background
        border: hasColoredBackground 
          ? 'none' // No border for colored backgrounds
          : '2px solid',
        borderColor: hasColoredBackground 
          ? 'transparent' 
          : showGrayedOut 
            ? darkGray
            : `${color}.main`,
        position: 'relative',
        zIndex: 2, // Higher z-index to ensure it's above the timeline line
        opacity: opacity,
        // Add a subtle shadow to help separate from timeline
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        // Add pulse animation for in-progress steps
        ...(status === STEP_STATUS.IN_PROGRESS && {
          "@keyframes pulse": {
            "0%": { boxShadow: "0 0 0 0 rgba(25, 118, 210, 0.7)" },
            "70%": { boxShadow: "0 0 0 10px rgba(25, 118, 210, 0)" },
            "100%": { boxShadow: "0 0 0 0 rgba(25, 118, 210, 0)" },
          },
          animation: "pulse 2s infinite",
        })
      }}
    >
      <IconComponent 
        sx={{ 
          fontSize: { sm: 22, md: 24 }, // has593v2 — slightly smaller glyph on tablet; desktop unchanged
          color: hasColoredBackground 
            ? 'white' // White icons on colored backgrounds
            : showGrayedOut 
              ? darkGray // Darker grey for better contrast on pending steps
              : `${color}.main` // Theme color for pending steps
        }} 
      />
    </Mui.Box>
  );
}

export default StepIcon;